import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  User,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [mobileNo, setMobileNo] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleLogin = async () => {
    if (!mobileNo.trim()) {
      alert("Please enter mobile number");
      return;
    }

    if (!password.trim()) {
      alert("Please enter password");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/User/Login`,
        {
          mobileNo,
          password,
        }
      );

      const data = response.data;

      login(
        {
          userId: data.userId,
          userName: data.userName,
          mobileNo: data.mobileNo,
        },
        data.token
      );

      navigate("/dashboard");
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.message ||
          "Invalid Mobile Number or Password"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid lg:grid-cols-2">
          {/* Left Side */}
          <div className="hidden lg:flex flex-col justify-center bg-slate-900 text-white p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-16 w-16 rounded-2xl bg-blue-600 flex items-center justify-center">
                <ShieldCheck size={32} />
              </div>

              <div>
                <h1 className="text-3xl font-bold">
                  Backup Manager
                </h1>
                <p className="text-slate-400">
                  Secure Data Protection
                </p>
              </div>
            </div>

            <h2 className="text-4xl font-bold leading-tight">
              Protect Your Business Data
            </h2>

            <p className="mt-6 text-slate-300">
              Manage backups, monitor storage usage,
              download archives, and keep your data safe
              from one centralized dashboard.
            </p>
          </div>

          {/* Right Side */}
          <div className="p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-800">
                Welcome Back
              </h2>

              <p className="text-slate-500 mt-2">
                Login to continue
              </p>
            </div>

            <div className="space-y-5">
              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Mobile Number
                </label>

                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-4 top-4 text-slate-400"
                  />

                  <input
                    type="text"
                    value={mobileNo}
                    onChange={(e) =>
                      setMobileNo(e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                    placeholder="Enter Mobile Number"
                    className="w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Password
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-4 text-slate-400"
                  />

                  <input
                    type={
                      showPassword ? "text" : "password"
                    }
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                    placeholder="Enter Password"
                    className="w-full pl-11 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(!showPassword)
                    }
                    className="absolute right-4 top-3"
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-xl font-semibold transition"
              >
                {loading ? "Please Wait..." : "Login"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;